import { Check } from '@emotion-icons/fa-solid';
import { ArrowDropDown } from '@emotion-icons/remix-line';
import styled from '@emotion/styled';
import { useState } from 'react';
import ClickOutside from './ClickOutside';
import SearchIcon from '~/icons/SearchIcon';

interface Option {
  label: string;
  value: string;
  symbol?: string;
  miniLabel?: string;
  logo?: React.FC; // Accept a React component
}

interface DropdownInputProps {
  value: string;
  options: Option[];
  onChange: (label: string) => void;
  search?: boolean;
  disabled?: boolean;
  className?: string;
  isMini?: boolean;
  showSelection?: boolean;
}

const DropdownWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const StyledDropdown = styled.div<{ disabled?: boolean }>`
  height: 44px;
  width: 100%;
  border-radius: 4px;
  border: 1px solid var(--border1);
  padding: 10px 16px;
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 500;
  text-align: left;
  background: ${props => (props.disabled ? '#f0f0f0' : 'white')};
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${props => (props.disabled ? '#a0a0a0' : 'black')};

  &:focus,
  &:hover {
    border: ${props => (props.disabled ? '1px solid #ccced0' : '1px solid var(--prim4)')};
    outline: none;
  }
`;

const DropdownIcon = styled.span<{ open: boolean }>`
  font-size: 14px;
  transform: ${props => (props.open ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const DropdownContainer = styled.div`
  position: absolute;
  width: 100%;
  background: white;
  border: 1px solid var(--border1);
  border-radius: 4px;
  margin-top: 4px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  z-index: 3;
`;

const DropdownList = styled.ul`
  max-height: 200px;
  overflow-y: auto;
  list-style: none;
  padding: 6px;
`;

const DropdownItem = styled.li<{ selected?: boolean }>`
  border-radius: 4px;
  padding: 12px 10px;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: var(--raisin-black);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${props => (props.selected ? 'var(--neut7)' : 'white')};
  margin: 4px 0;
  &:first-of-type {
    margin-top: 0;
  }

  &:hover {
    background: var(--neut7);
  }
`;

const OptionContent = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1;
  overflow: hidden;
`;

const LogoContainer = styled.div`
  margin-right: 8px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
`;

const LabelText = styled.p<{ isPlaceholder?: boolean }>`
  white-space: nowrap;
  overflow-x: hidden;
  line-height: normal;
  text-overflow: ellipsis;
  flex-grow: 1;
  color: ${props => (props.isPlaceholder ? '#a0a0a0' : 'inherit')};
`;

const SelectedTick = styled(Check)`
  color: green;
  width: 16px;
  height: 16px;
  margin-left: 8px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px;
  padding-right: 40px;
  border: none;
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  outline: none;

  &:focus {
    border-bottom: 1px solid var(--prim4);
  }
`;

const SearchContainer = styled.div`
  position: sticky;
  top: 0;
  background: white;
  padding: 10px;
  border-bottom: 1px solid var(--border1);
  position: relative;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #a0a0a0;
`;

const DropdownInput: React.FC<DropdownInputProps> = ({
  value,
  options,
  onChange,
  search = false,
  disabled = false,
  className = '',
  isMini = false,
  showSelection = true,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  let selectedOption = options.find(opt => opt.value === value);
  const filteredOptions = options.filter(option =>
    option.value.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  if (!selectedOption && value) {
    selectedOption = { value: value, label: value };
  }

  return (
    <ClickOutside onClose={() => setOpen(false)} className="full-width">
      <DropdownWrapper>
        <StyledDropdown
          className={className}
          disabled={disabled}
          onClick={() => !disabled && setOpen(!open)}
        >
          <OptionContent>
            {selectedOption?.logo && (
              <LogoContainer>
                <selectedOption.logo />
              </LogoContainer>
            )}
            <LabelText isPlaceholder={!selectedOption}>
              {(isMini ? selectedOption?.miniLabel : selectedOption?.label) ||
                (isMini ? 'Select' : 'Select an option')}
            </LabelText>
          </OptionContent>
          <DropdownIcon open={open}>
            <ArrowDropDown size={30} />
          </DropdownIcon>
        </StyledDropdown>

        {open && !disabled && (
          <DropdownContainer>
            {search && (
              <SearchContainer>
                <SearchInputWrapper>
                  <SearchInput
                    autoFocus
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                  <SearchIconWrapper>
                    <SearchIcon size={20} stroke="#a0a0a0" />
                  </SearchIconWrapper>
                </SearchInputWrapper>
              </SearchContainer>
            )}

            <DropdownList>
              {filteredOptions.map(option => (
                <DropdownItem
                  key={option.value + option.label}
                  selected={option.value === value}
                  onClick={() => {
                    setOpen(false);
                    setSearchQuery('');
                    if (option.value != value) {
                      onChange(option.value);
                    }
                  }}
                >
                  <OptionContent>
                    {option.logo && (
                      <LogoContainer>
                        <option.logo />
                      </LogoContainer>
                    )}
                    <LabelText>{isMini ? option.miniLabel : option.label}</LabelText>
                  </OptionContent>
                  {showSelection && option.value === value && <SelectedTick />}
                </DropdownItem>
              ))}
              {filteredOptions.length === 0 && <DropdownItem>No results found</DropdownItem>}
            </DropdownList>
          </DropdownContainer>
        )}
      </DropdownWrapper>
    </ClickOutside>
  );
};

export default DropdownInput;
